import {
  buildAddOutcome,
  deriveOutcomeMintPda,
  utf8ByteLength,
  validateOutcomeMetadataText,
} from '@morfi/contract-sdk';
import { type Connection, PublicKey } from '@solana/web3.js';
import type { SigningDeps } from '../auth/turnkey/signing-types.js';
import type { TxEngineLogger } from '../tx-engine/config.js';
import {
  createTransactionTraceId,
  hashDiagnosticEntity,
} from '../tx-engine/diagnostics/lifecycle.js';
import {
  safeRecoveryActionType,
  safeTxErrorCode,
  safeTxStage,
} from '../tx-engine/errors/errors.js';
import { resolveRelayFeePayer } from '../tx-engine/relay/fee-payer.js';
import type { TurnkeyDeps } from '../tx-engine/turnkey-types.js';
import type { ActiveWallet, SubmissionOptions, SubmissionResult } from '../tx-engine/types.js';
import {
  type AddOutcomeParams,
  type AddOutcomeProgressState,
  type AddOutcomeResult,
  INITIAL_ADD_OUTCOME_PROGRESS_STATE,
} from './add-outcome-types.js';
import { type SubmitTransactionFn, toEngineBuildResult } from './orchestrator-build.js';
import { retryTransientSubmission } from './retry-submission.js';
import { txSubmitError } from './tx-submit-error.js';

const NOOP_LOGGER: TxEngineLogger = {
  info: (): void => {},
  warn: (): void => {},
  error: (): void => {},
};

type SubmissionMetadata = NonNullable<SubmissionOptions['metadata']>;

export interface RunAddOutcomeArgs {
  readonly connection: Connection;
  readonly submitTransaction: SubmitTransactionFn;
  readonly activeWallet: ActiveWallet;
  readonly signingDeps: SigningDeps;
  readonly turnkeyDeps: TurnkeyDeps;
  readonly params: AddOutcomeParams;
  readonly signal?: AbortSignal;
  readonly onProgress?: (snapshot: AddOutcomeProgressState) => void;
  readonly logger?: TxEngineLogger;
}

class AddOutcomeAbort extends Error {
  override readonly name = 'AddOutcomeAbort';
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new AddOutcomeAbort('Add-outcome aborted');
}

async function sleepOrAbort(delayMs: number, signal: AbortSignal | undefined): Promise<void> {
  if (delayMs <= 0) return;
  throwIfAborted(signal);
  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = (): void => {
      if (timer !== undefined) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = (): void => {
      cleanup();
      reject(new AddOutcomeAbort('Add-outcome aborted'));
    };
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, delayMs);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function operationalErrorClass(error: unknown): 'Error' | 'non_error' {
  return error instanceof Error ? 'Error' : 'non_error';
}

function submissionFailureLog(
  metadata: SubmissionMetadata,
  result: Extract<SubmissionResult, { ok: false }>,
): Record<string, unknown> {
  return {
    ...metadata,
    errorClass: 'TxError',
    errorCode: safeTxErrorCode(result.error.code),
    errorStage: safeTxStage(result.error.stage),
    recoveryAction: safeRecoveryActionType(result.error.recoveryAction.type),
  };
}

export async function runAddOutcome(args: RunAddOutcomeArgs): Promise<AddOutcomeResult> {
  const { connection, submitTransaction, activeWallet, signingDeps, turnkeyDeps, params, signal } =
    args;
  const onProgress = args.onProgress ?? ((): void => {});
  try {
    validateOutcomeMetadataText(params);
  } catch (error) {
    args.logger?.warn('add_outcome.metadata_text_invalid', {
      nameBytes: utf8ByteLength(params.name),
      symbolBytes: utf8ByteLength(params.symbol),
      uriBytes: utf8ByteLength(params.uri),
    });
    throw error;
  }
  const creator = new PublicKey(activeWallet.address);
  const market = new PublicKey(params.marketPubkeyBase58);
  const marketConfig = new PublicKey(params.marketConfigBase58);
  const eventSchedule =
    params.eventScheduleBase58 === null ? null : new PublicKey(params.eventScheduleBase58);
  const [outcomeMint] = deriveOutcomeMintPda(market, params.outcomeIndex);
  const workflowTraceId = createTransactionTraceId();
  const workflowMetadata: SubmissionMetadata = {
    addOutcomeWorkflowTraceId: workflowTraceId,
    marketHash: hashDiagnosticEntity(params.marketPubkeyBase58),
    outcomeMintHash: hashDiagnosticEntity(outcomeMint.toBase58()),
    outcomeIndex: params.outcomeIndex,
    tickerHash: hashDiagnosticEntity(params.ticker),
    uriHash: hashDiagnosticEntity(params.uri),
  };

  let state: AddOutcomeProgressState = {
    ...INITIAL_ADD_OUTCOME_PROGRESS_STATE,
    status: 'preparing',
    outcomeMintBase58: outcomeMint.toBase58(),
  };
  onProgress(state);

  let relayFeePayerBase58: string;
  try {
    relayFeePayerBase58 = await resolveRelayFeePayer(args.logger ?? NOOP_LOGGER);
  } catch (error) {
    state = {
      ...state,
      status: 'failed',
      error: {
        stage: 'preparing',
        message: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200),
      },
    };
    onProgress(state);
    args.logger?.error('add_outcome.relay_fee_payer_resolve_failed', {
      ...workflowMetadata,
      errorClass: operationalErrorClass(error),
      errorCode: 'AO_RELAY_FEE_PAYER_RESOLVE_FAILED',
      errorStage: 'preparing',
    });
    throw error;
  }

  throwIfAborted(signal);
  let build: Awaited<ReturnType<typeof buildAddOutcome>>;
  try {
    build = await buildAddOutcome(connection, {
      creator,
      payer: new PublicKey(relayFeePayerBase58),
      market,
      marketConfig,
      eventSchedule,
      outcome: {
        outcomeIndex: params.outcomeIndex,
        name: params.name,
        symbol: params.symbol,
        uri: params.uri,
      },
    });
  } catch (error) {
    args.logger?.error('add_outcome.builder_failed', {
      ...workflowMetadata,
      errorClass: operationalErrorClass(error),
      errorCode: 'AO_BUILDER_FAILED',
      errorStage: 'building',
    });
    throw error;
  }

  state = { ...state, status: 'outcome' };
  onProgress(state);
  const txTraceId = createTransactionTraceId();
  const metadata: SubmissionMetadata = { ...workflowMetadata, txTraceId };
  const result = await retryTransientSubmission({
    submitTransaction,
    buildResult: toEngineBuildResult(build),
    options: {
      signingDeps,
      turnkeyDeps,
      activeWallet,
      instructionName: 'add_outcome',
      traceId: txTraceId,
      metadata,
      forceRelay: true,
      expectedRelayFeePayer: relayFeePayerBase58,
      ...(signal === undefined ? {} : { signal }),
    },
    retryContext: { workflow: 'add_outcome', workflowTraceId },
    signal,
    throwIfAborted,
    sleepOrAbort,
    onRetryScheduled: ({ attempt, delayMs, result: failedResult }) =>
      args.logger?.warn('add_outcome.submit_retry_scheduled', {
        ...submissionFailureLog(metadata, failedResult),
        attempt,
        delayMs,
      }),
  });

  if (!result.ok) {
    const message = result.error.morfiError ?? result.error.userMessageKey ?? 'add_outcome failed';
    state = { ...state, status: 'failed', error: { stage: 'outcome', message } };
    onProgress(state);
    args.logger?.error('add_outcome.failed', submissionFailureLog(metadata, result));
    throw txSubmitError(`add_outcome failed: ${message}`, result.error);
  }

  state = {
    ...state,
    status: 'done',
    outcomeSubmitted: result.signature,
    outcomeConfirmed: true,
  };
  onProgress(state);
  return {
    marketPubkey: params.marketPubkeyBase58,
    outcomeIndex: params.outcomeIndex,
    outcomeMintBase58: outcomeMint.toBase58(),
    signature: result.signature,
  };
}
